/**
 * Formatting utilities for dates, times, pace, and other metrics
 */

/**
 * Format timestamp to localized date string
 * @param {number|string} timestamp - Timestamp in milliseconds or ISO string
 * @returns {string} Formatted date string
 */
export function formatDate (timestamp) {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Format timestamp to localized date string for events
 * @param {number} timestamp - Timestamp in milliseconds
 * @returns {string} Formatted date string
 */
export function formatEventDate (timestamp) {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Format timestamp to ISO date string (YYYY-MM-DD)
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Formatted date string
 */
export function formatDateTime (timestamp) {
  return new Date(timestamp).toISOString().split('T')[0]
}

/**
 * Format Unix timestamp to localized datetime string
 * @param {number} timestamp - Unix timestamp in seconds
 * @returns {string} Formatted datetime string
 */
export function formatTimestamp (timestamp) {
  if (!timestamp) return 'Unknown'
  const date = new Date(timestamp * 1000)
  return date.toLocaleString()
}

/**
 * Format time in seconds to HH:MM:SS or MM:SS
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
export function formatTime (seconds) {
  if (!seconds || seconds === 0) return 'N/A'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  } else {
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }
}

/**
 * Format moving time in seconds to HH:MM:SS or MM:SS
 * @param {number} seconds - Moving time in seconds
 * @returns {string} Formatted time string
 */
export function formatMovingTime (seconds) {
  if (!seconds) return '-'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  } else {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }
}

/**
 * Format activity pace (min/km)
 * @param {Object} activity - Activity object with moving_time and km properties
 * @returns {string} Formatted pace string (MM:SS/km)
 */
export function formatPace (activity) {
  if (!activity.moving_time || !activity.km) return '-'
  const paceSeconds = activity.moving_time / activity.km
  const minutes = Math.floor(paceSeconds / 60)
  const seconds = Math.floor(paceSeconds % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}/km`
}

/**
 * Check if event is upcoming
 * @param {number} timestamp - Timestamp in milliseconds
 * @returns {boolean} True if event is in the future
 */
export function isUpcomingEvent (timestamp) {
  return new Date(timestamp) > new Date()
}

/**
 * Check if event is expired
 * @param {number} timestamp - Timestamp in milliseconds
 * @returns {boolean} True if event is in the past
 */
export function isExpiredEvent (timestamp) {
  return new Date(timestamp) < new Date()
}
